import React, { useId, useState } from "react";
import { ChevronDown } from "lucide-react";

export interface AccordionSectionProps {
  /** What the section is called, in the heading row that opens it. */
  title: string;
  /** The grey line under the title — what is in here and why it can be left. */
  description?: string;
  /**
   * Whether it starts open. Left `false` for a group of questions nobody has
   * to answer: a form that opens showing every optional field it could ever
   * ask reads as a longer form than it is.
   */
  defaultOpen?: boolean;
  /**
   * How many answers are already in it, drawn as a count beside the title.
   * `0` draws nothing — a badge saying "0 filled" is noise on a section that
   * is meant to be skippable.
   */
  filledCount?: number;
  children: React.ReactNode;
}

/**
 * A `FormSection` that folds — the shell an optional group of questions sits
 * in.
 *
 * The card is tinted rather than white, which is the one place a long form
 * changes materials on purpose: everything above it is asked for, and this is
 * the part that may be walked past. The colour is what says so at a glance,
 * before the "(optional)" in the title has been read — a white card in the
 * same stack reads as one more thing to fill in. It carries no title caption
 * above it either, for the same reason: the sections above are steps through
 * the form, and this is an aside beside them.
 *
 * The title sits inside the card and is a button, because what is behind the
 * fold is not part of what the form is asking for and a required field would
 * be wrong to hide.
 *
 * The panel is left mounted and hidden rather than unmounted, so anything
 * typed into it survives being folded away — a section that lost its answers
 * on a stray click would be worse than one that never folded at all. It is
 * `hidden`, so nothing inside is reachable by keyboard or read out while it is
 * shut.
 */
export const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  description,
  defaultOpen = false,
  filledCount = 0,
  children,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const panelId = `${useId()}-panel`;

  return (
    <section className="rounded-lg border border-[#D8C2F0] bg-[#FDFBFF]">
      <h2>
        {/* Roomier than the white cards above it — the fold is a single row
            standing in for a whole section, and the air is what stops it
            reading as a stray banner dropped under the form. */}
        <button
          type="button"
          aria-expanded={open}
          aria-controls={panelId}
          onClick={() => setOpen((current) => !current)}
          className="flex w-full items-center gap-3 rounded-lg px-4 py-4 text-left sm:px-5 sm:py-5 3xl:px-6 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#B98BE8]/50"
        >
          <span className="min-w-0 flex-1">
            <span className="block text-[15px] 3xl:text-[16px] font-medium text-ink">
              {title}
            </span>
            {description && (
              <span className="mt-1 block text-[13px] 3xl:text-[14px] text-ink-muted">
                {description}
              </span>
            )}
          </span>

          {/* What is already in there, so a section that has been filled and
              folded back up still says so rather than reading as untouched.
              In the card's own colour rather than the brand's: a teal pill on
              a lilac card reads as a status put there by something else. */}
          {filledCount > 0 && (
            <span className="flex-shrink-0 rounded-full border border-[#D8C2F0] bg-[#F6EFFD] px-2.5 py-0.5 text-[12px] font-medium text-[#7040AE]">
              {filledCount} added
            </span>
          )}

          {/* Kept, though the row would read as a card without it: the tint
              says the section is optional, and only the chevron says it opens
              at all. */}
          <ChevronDown
            size={18}
            aria-hidden="true"
            className={
              "flex-shrink-0 text-[#8B5CF6] transition-transform " +
              (open ? "rotate-180" : "")
            }
          />
        </button>
      </h2>

      <div
        id={panelId}
        hidden={!open}
        className="border-t border-[#EADFF8] px-4 py-4 sm:px-5 sm:py-5 3xl:px-6"
      >
        {children}
      </div>
    </section>
  );
};

export default AccordionSection;
